/**
 * Stroked inline SVG on a 24 grid, inheriting `currentColor` and sized in `em`
 * so it matches the type beside it.
 *
 * All icons are aria-hidden. A control whose only content is an icon needs its
 * own aria-label.
 */
import type { ReactNode, SVGProps } from 'react'

export type IconName =
  | 'search'
  | 'settings'
  | 'chevronRight'
  | 'chevronDown'
  | 'chevronUp'
  | 'chevronLeft'
  | 'close'
  | 'plus'
  | 'paperclip'
  | 'trash'
  | 'pill'
  | 'image'
  | 'file'
  | 'rotate'
  | 'zoomIn'
  | 'zoomOut'
  | 'alert'
  | 'info'
  | 'check'
  | 'userPlus'
  | 'users'
  | 'cross'
  | 'signOut'
  | 'bell'
  | 'phone'
  | 'pin'
  | 'calendar'
  | 'calendarDays'
  | 'upload'
  | 'save'
  | 'more'
  | 'home'
  | 'chart'
  | 'eye'
  | 'eyeOff'
  | 'shield'
  | 'lock'
  | 'plane'
  | 'menu'

/** A dot, drawn as a zero-length round-capped line so it keeps the stroke weight. */
const dot = (x: number, y: number) => <path d={`M${x} ${y}h0.01`} />

const ICONS: Record<IconName, ReactNode> = {
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5 21 21" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3.25" />
      <circle cx="12" cy="12" r="8" />
      <path d="M12 2.5v2.2M12 19.3v2.2M21.5 12h-2.2M4.7 12H2.5M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6M18.7 18.7l-1.6-1.6M6.9 6.9 5.3 5.3" />
    </>
  ),
  chevronRight: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  chevronLeft: <path d="M14.5 5.5 8 12l6.5 6.5" />,
  chevronDown: <path d="m5.5 9.5 6.5 6.5 6.5-6.5" />,
  chevronUp: <path d="m5.5 14.5 6.5-6.5 6.5 6.5" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  paperclip: (
    <path d="M18 10.5 10.8 17.7a4 4 0 0 1-5.7-5.7l7.8-7.8a2.7 2.7 0 0 1 3.8 3.8l-7.8 7.8a1.4 1.4 0 0 1-1.9-1.9l7-7" />
  ),
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M6.5 7 7.4 20a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
      <path d="M10.5 11v6M13.5 11v6" />
    </>
  ),
  pill: (
    <>
      <rect x="2.5" y="8" width="19" height="8" rx="4" transform="rotate(-45 12 12)" />
      <path d="m9.2 9.2 5.6 5.6" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="8.5" cy="9.5" r="1.6" />
      <path d="m3.5 18.5 4.6-4.6 3.4 3.4L15 13.7l5.5 5.5" />
    </>
  ),
  file: (
    <>
      <path d="M14 3H7.5A2 2 0 0 0 5.5 5v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7.5Z" />
      <path d="M14 3v4.5h4.5" />
      <path d="M9 13h6M9 16.5h4" />
    </>
  ),
  rotate: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" />
      <path d="m13.4 5.7 4 1 1-4" />
    </>
  ),
  zoomIn: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5 21 21M8 11h6M11 8v6" />
    </>
  ),
  zoomOut: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5 21 21M8 11h6" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3.6 21.2 19.6a1 1 0 0 1-.9 1.5H3.7a1 1 0 0 1-.9-1.5Z" />
      <path d="M12 9.5v4.5" />
      {dot(12, 17.5)}
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11.5v5" />
      {dot(12, 8)}
    </>
  ),
  check: <path d="m4.8 12.5 4.9 4.9L19.2 7.9" />,
  userPlus: (
    <>
      <circle cx="9.5" cy="8" r="3.5" />
      <path d="M3 20a6.5 6.5 0 0 1 13 0" />
      <path d="M19 8.5v6M16 11.5h6" />
    </>
  ),
  users: (
    <>
      <circle cx="9.5" cy="8" r="3.5" />
      <path d="M2.5 20a7 7 0 0 1 14 0" />
      <path d="M16.8 4.9a3.5 3.5 0 0 1 0 6.6" />
      <path d="M18 14.2a5.6 5.6 0 0 1 3.5 5.2" />
    </>
  ),
  /* The brand mark. Filled, because it sits inside a solid accent tile where an
     outline would read as a hairline box rather than a medical cross. */
  /* Arms span 5–19 on both axes: the glyph's own centre is the box's
     centre, so `place-items-center` actually centres it. Drawn 3–17 it
     sat two units high in every lockup. */
  cross: <path d="M10 5h4v5h5v4h-5v5h-4v-5H5v-4h5Z" fill="currentColor" stroke="none" />,
  /* Door on the left, arrow leaving it. Drawn the other way round it reads as
     "sign in", which is the opposite of what the control does. */
  signOut: (
    <>
      <path d="M9.5 3.5H7A2.5 2.5 0 0 0 4.5 6v12A2.5 2.5 0 0 0 7 20.5h2.5" />
      <path d="M10 12h9.5" />
      <path d="m15.9 8.2 3.8 3.8-3.8 3.8" />
    </>
  ),
  bell: (
    <>
      <path d="M18 9a6 6 0 1 0-12 0c0 4.8-1.8 6.3-1.8 6.3h15.6S18 13.8 18 9Z" />
      <path d="M13.8 18.8a2.1 2.1 0 0 1-3.6 0" />
    </>
  ),
  phone: (
    <path d="M6.3 3.5h3.1l1.5 3.8-1.9 1.3a12.6 12.6 0 0 0 5.4 5.4l1.3-1.9 3.8 1.5v3.1a2.2 2.2 0 0 1-2.4 2.2A15.6 15.6 0 0 1 4.1 5.9a2.2 2.2 0 0 1 2.2-2.4Z" />
  ),
  pin: (
    <>
      <path d="M12 21.5c4.7-4.8 7-8.4 7-11.4a7 7 0 1 0-14 0c0 3 2.3 6.6 7 11.4Z" />
      <circle cx="12" cy="10" r="2.6" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  calendarDays: (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
      {dot(8, 14)}
      {dot(12, 14)}
      {dot(16, 14)}
      {dot(8, 17.5)}
      {dot(12, 17.5)}
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4" />
      <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
      <path d="M4 15v3.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V15" />
    </>
  ),
  save: (
    <>
      <path d="M5.5 3.5h11l4 4V18a2.5 2.5 0 0 1-2.5 2.5H5.5A2.5 2.5 0 0 1 3 18V6a2.5 2.5 0 0 1 2.5-2.5Z" />
      <path d="M8 3.5V9h7V3.5" />
      <path d="M7 20.5v-6h10v6" />
    </>
  ),
  more: (
    <g fill="currentColor" stroke="none">
      <circle cx="12" cy="5" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="12" cy="19" r="1.4" />
    </g>
  ),
  home: (
    <>
      <path d="M3.8 10.4 12 3.5l8.2 6.9V19a2 2 0 0 1-2 2H5.8a2 2 0 0 1-2-2Z" />
      <path d="M9.5 21v-6.2h5V21" />
    </>
  ),
  chart: (
    <>
      <path d="M4 3.5v17h16.5" />
      <path d="M8.5 17v-4.5M13 17V7.5M17.5 17v-7" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.7-6.5 10-6.5S22 12 22 12s-3.7 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M10.8 6.2A11.4 11.4 0 0 1 12 5.5c6.3 0 10 6.5 10 6.5a18.2 18.2 0 0 1-3.5 4.2" />
      <path d="M6.7 7.9A17.6 17.6 0 0 0 2 12s3.7 6.5 10 6.5a11 11 0 0 0 4.1-.8" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m3.5 3.5 17 17" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 5 5.8v5.4c0 4.4 2.9 8.3 7 9.6 4.1-1.3 7-5.2 7-9.6V5.8Z" />
      <path d="m9 11.8 2.2 2.2 4-4" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="10" width="15" height="10.5" rx="2.5" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  plane: (
    <path d="M10.6 3.2a1.4 1.4 0 0 1 2.8 0V9l7.6 4.4v2.2l-7.6-2.3v4.2l2.4 1.7v1.3L12 19.8l-3.8.7v-1.3l2.4-1.7v-4.2L3 15.6v-2.2L10.6 9Z" />
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
}

interface Props extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
  /** Matches the surrounding type by default. */
  size?: number | string
}

export function Icon({ name, size = '1.25em', className, ...rest }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={{ flex: 'none', display: 'block' }}
      {...rest}
    >
      {ICONS[name]}
    </svg>
  )
}
